import * as React from 'react'

import { comparer, computed, IEqualsComparer } from 'mobx'
import { observer } from 'mobx-react'
import { Layer, Source } from 'react-map-gl/mapbox'

// Not the '~/client/graph' barrel: it instantiates Apollo, and the federated viewer mounts this.
import {
  SitemapItemShapeType,
  SitemapLineArrowPosition,
} from '~/client/graph/types.generated'
import {
  BasemapInk,
  labelMeasureFont,
  LOCATION_LABEL,
} from '~/client/src/shared/map/labelLayerStyle'
import { getLabelLiftPx } from '~/client/src/shared/map/locationLabelGeometry'
import { stackLocations } from '~/client/src/shared/map/locationStacks'
import {
  getZoomScale,
  MAX_ITEM_ZOOM_LEVEL,
  PROJECT_MARKER_ZOOM_LEVEL,
} from '~/client/src/shared/map/mapZoomRule'

import ThemeMode from '../../../utils/ThemeModeManager'
import { IGroundContext } from '../../MapBoxViewer/labelGroundTone'
import { IPlateDecision } from '../../MapBoxViewer/labelPlateDecision'
import { measureLabelTextBox } from '../../MapBoxViewer/labelTextBox'
import {
  LOCATION_MARKERS_LAYER,
  LOCATION_MARKERS_SELECTED_LAYER,
  LOCATION_MARKERS_SOURCE,
  mapViewItemKey,
  SHAPE_ARROW_LINE_LAYER,
  SHAPE_ARROW_LINE_SOURCE,
  SHAPE_FILL_LAYER,
  SHAPE_FILL_SOURCE,
  SHAPE_LINE_LAYER,
  SHAPE_LINE_SOURCE,
} from '../../MapBoxViewer/mapboxConstants'
import { TEXT_OCCLUSION_PAINT } from '../../MapBoxViewer/mapOcclusionRule'
import {
  ILabelAnchor,
  LabelBoxAt,
  useLabelGroundTones,
} from '../../MapBoxViewer/useLabelGroundTones'
import MapViewItemBase from '../models/MapViewItemBase'
import {
  GLLocationMarkerLayer,
  LocationMarkerPart,
} from '../utils/GLLocationMarkerLayer'
import {
  buildLocationMarkerGeoJson,
  ILocationMarkerParts,
  maskLocationMarkerParts,
  placeLocationMarkers,
} from '../utils/GLLocationMarkerSource'
import { ILocationClosureContext } from '../utils/locationClosureState'
import { groundFillsFromLocations } from '../utils/locationGroundFills'
import { hasDisplayedMarker } from '../utils/markerDisplay'
import { IPlanFootprint } from '../utils/planFootprints'
import GLProjectAddressMarker from './GLProjectAddressMarker'

export interface ILocationLayerDisplay extends ILocationMarkerParts {
  shapes: boolean
  forceMarkerOverlap?: boolean
  // Where to draw an icon instead of its own point, by itemId (an export moving overlaps apart).
  markerPositions?: ReadonlyMap<string, [number, number]>
  iconScale?: number
  // The icons keep one size at any zoom (an export printing them at a set size).
  isIconSizeFixed?: boolean
  // Hands out the ink read for each label, for a host that draws the labels itself.
  onLabelTones?: (tones: Map<string, IPlateDecision>) => void
  // Changes when the ground under the labels changes with neither the camera nor a source saying
  // so (an export hiding a map file), so every label is read again.
  labelRerunKey?: string
  // Which locations on one point draw as one marker with its count, for a host with no list to
  // open them in (an export, which fans a few equal-status ones apart instead).
  stackWhen?: (locationIds: string[]) => boolean
}

const EMPTY_SHAPES: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
  type: 'FeatureCollection',
  features: [],
}

interface IProps {
  items: MapViewItemBase[]
  // From isProjectMarkerOnlyZoom(). A boolean, so the crossing re-renders, not every frame.
  isProjectMarkerOnly: boolean
  // Absent only for a project with no geocoded address.
  projectCoordinates?: { latitude: number; longitude: number }
  // Omitted by hosts whose data cannot express closures; the marker then draws no badge.
  closureContext?: ILocationClosureContext
  isZoomScalingEnabled?: boolean
  isLabelOverlapEnabled?: boolean
  // What the labels stand on, so an unplated one can pick ink that reads against it. Optional:
  // hosts that cannot describe their ground leave every unplated label on the basemap guess,
  // which is what the reader falls back to per label anyway.
  ground?: IGroundContext
  // REQUIRED on purpose: a host that is not asked falls back to a light constant and puts dark
  // ink on satellite, and a compiler error is the only reliable way to stop the next one. A
  // scalar, not an object, so its identity cannot churn the ground memos below.
  basemapTone: BasemapInk
  // Where the plan sheets are. A location label standing on one is plated whatever its box
  // checkbox says, because correct ink alone is not enough over dense linework.
  planFootprints?: IPlanFootprint[]
  // The item the editor is working on. It renders exactly as it will live, with a ring around
  // it — rather than being excluded here and redrawn as a DOM lookalike.
  outlinedItemId?: string
  shouldHideOutline?: boolean
  offViewItemId?: string
  highlightedItemId?: string
  // Mounts the selected tier's copies, so a host that lifts never adds layers on a click.
  liftsSelection?: boolean
  // The location whose card is open.
  liftedItemId?: string
  yieldToLayerIds?: string[]
  onHoverItem?: (itemId: string | null) => void
  onClickItem?: (itemId: string) => void
  // Opts in to stacking locations on identical coordinates into one marker that lists them.
  onClickStack?: (stack: mapboxgl.GeoJSONFeature) => void
  onDoubleClickItem?: (itemId: string, part: LocationMarkerPart) => void
  // Which parts draw. Unset draws everything.
  display?: ILocationLayerDisplay
}

// The builders read observables per item, so they go through a MobX computed — a cached useMemo
// reads nothing and misses edits. `equals` is how a derivation stops PROPAGATING a value it had
// to recompute, which is what keeps the identity handed to react-map-gl stable. NoInfer, or a
// comparer typed over `any` widens T and the caller loses the build's own type.
const useTrackedMemo = <T,>(
  build: () => T,
  deps: React.DependencyList,
  equals?: IEqualsComparer<NoInfer<T>>,
): T => React.useMemo(() => computed(build, { equals }), deps).get()

export const MapLocationLayers = observer(function MapLocationLayers({
  items,
  isProjectMarkerOnly,
  projectCoordinates,
  closureContext,
  isZoomScalingEnabled,
  isLabelOverlapEnabled,
  ground,
  basemapTone,
  planFootprints,
  outlinedItemId,
  shouldHideOutline,
  offViewItemId,
  highlightedItemId,
  liftsSelection,
  liftedItemId,
  yieldToLayerIds,
  onHoverItem,
  onClickItem,
  onClickStack,
  onDoubleClickItem,
  display,
}: IProps): JSX.Element {
  // Shallow, because the `!!coordinates` test subscribes to the coordinate OBJECT BOX and a drag
  // replaces that object every frame — without the comparer this re-derives everything keyed on
  // it per pointer sample, invisibly, since react-map-gl then concludes nothing changed.
  // Membership still propagates, and a MOVE is seen by the builders reading `coordinates`.
  const markerItems = useTrackedMemo(
    () =>
      items.filter(i => !!i.sitemapItem.coordinates && hasDisplayedMarker(i)),
    [items],
    comparer.shallow,
  )

  // Only the UNPLATED labels: a plated one keeps the location's colour, so reading the ground
  // under it would answer a question nobody asked.
  const labelAnchors = useTrackedMemo(
    () =>
      markerItems
        .filter(
          i =>
            i.globeViewItemProperties.labelProperties?.isDisplayed &&
            // The editor's checkbox is the one plate that needs no reading: it is asked for
            // outright, and a plated label's ink is constant. Everything else must be read even
            // if it ends up plated, because the reading is now what DECIDES the plate.
            !i.globeViewItemProperties.labelProperties?.isTextBoxDisplayed,
        )
        .map(i => ({
          id: mapViewItemKey(i.id),
          longitude: i.sitemapItem.coordinates.longitude,
          latitude: i.sitemapItem.coordinates.latitude,
        })) as ILabelAnchor[],
    [markerItems],
  )
  const itemsById = React.useMemo(
    () => new Map(markerItems.map(i => [mapViewItemKey(i.id), i])),
    [markerItems],
  )
  // Every location carries its own font size, so the measurement is per item rather than per
  // tier — and the lift it hangs on is the label's, not the plate's, since these are unplated.
  const boxAt = React.useCallback<LabelBoxAt>(
    (anchor, zoom) => {
      const item = itemsById.get(anchor.id)
      const size = item?.globeViewItemProperties.labelProperties?.fontSize ?? 12
      const scale = getZoomScale(zoom)
      // The layer sets text-max-width to 1000, so a location label never wraps and the
      // measurement is always one line. The size is the item's own, the weight the tier's.
      const measured = measureLabelTextBox(
        item?.displayName ?? '',
        labelMeasureFont(LOCATION_LABEL, size),
        Number.MAX_SAFE_INTEGER,
        size,
      )
      // The LINE box, not the font size. LOCATION_LABEL is 14/12, so sizing the grid by `size`
      // made it ~14% shorter than the text it decides ink for — and since dy measures to the
      // box centre while getLabelLiftPx returns the distance to the line box TOP, it also sat
      // that much too high. Both sibling tiers already use their line height (pin labels
      // LABEL_LINE_HEIGHT_PX, markups MARKUP_LABEL.lineHeightPx); this is per-item because the
      // font size is user data, so the tier's ratio is applied to the item's own size.
      const lineHeight =
        size * (LOCATION_LABEL.lineHeightPx / LOCATION_LABEL.sizePx)
      const height = measured.lines * lineHeight * scale
      return {
        dx: 0,
        dy: getLabelLiftPx(size) * scale + height / 2,
        width: measured.width * scale,
        height,
      }
    },
    [itemsById],
  )
  const renderedLabels = React.useMemo(
    () => ({
      // Every layer that can carry a placed LABEL. The boxed pair is not optional: a plated
      // label still has to be read, because the read is what decides whether it STAYS plated.
      // Omit them and it gets no probes, and the no-probe branch carries the old decision
      // forward verbatim — plated for the session, with its ink frozen.
      layerId: [
        LOCATION_MARKERS_LAYER,
        `${LOCATION_MARKERS_LAYER}-label`,
        `${LOCATION_MARKERS_LAYER}-label-boxed`,
        `${LOCATION_MARKERS_LAYER}-label-boxed-center`,
        LOCATION_MARKERS_SELECTED_LAYER,
        `${LOCATION_MARKERS_SELECTED_LAYER}-label`,
        `${LOCATION_MARKERS_SELECTED_LAYER}-label-boxed`,
        `${LOCATION_MARKERS_SELECTED_LAYER}-label-boxed-center`,
      ],
      idProperty: 'itemId',
    }),
    [],
  )
  // DERIVED here rather than required from the host, so the five hosts cannot deviate. Two of
  // its three parts need no host at all — rasters come off the live style, location fills from
  // the very items this component is about to draw. A host opts IN to a richer ground (markup
  // fills); it does not opt in to having one.
  const derivedGround = useTrackedMemo<IGroundContext>(
    () => ({
      basemap: basemapTone,
      fills: groundFillsFromLocations(markerItems),
    }),
    [basemapTone, markerItems],
  )
  const effectiveGround = ground ?? derivedGround

  // Labels show without the camera moving when the card lifts one, or when the host takes them all
  // over from mapbox, so every one it draws is read rather than only those mapbox placed.
  const tones = useLabelGroundTones(
    labelAnchors,
    effectiveGround,
    boxAt,
    renderedLabels,
    true,
    `${liftedItemId ?? ''}|${display?.labels ?? true}|${display?.labelRerunKey ?? ''}`,
  )
  const onLabelTones = display?.onLabelTones
  React.useEffect(() => onLabelTones?.(tones), [onLabelTones, tones])

  const buildFor = useTrackedMemo(
    () => (subset: MapViewItemBase[]) =>
      buildLocationMarkerGeoJson(
        subset,
        closureContext,
        { tones, basemap: effectiveGround.basemap },
        planFootprints,
        outlinedItemId,
        shouldHideOutline,
        liftedItemId,
        offViewItemId,
      ),
    [
      closureContext,
      tones,
      effectiveGround,
      planFootprints,
      outlinedItemId,
      shouldHideOutline,
      liftedItemId,
      offViewItemId,
    ],
  )

  const markerGeoJson = useTrackedMemo(
    () => buildFor(markerItems),
    [buildFor, markerItems],
  )
  // Stacked before anything is moved: a lead an export moves apart would otherwise leave its
  // members behind on the point to stack again without it.
  const stackWhen = display?.stackWhen
  const stackedMarkerGeoJson = React.useMemo(
    () =>
      onClickStack
        ? stackLocations(markerGeoJson)
        : stackWhen
          ? stackLocations(markerGeoJson, stackWhen)
          : markerGeoJson,
    [markerGeoJson, onClickStack, stackWhen],
  )
  const layerData = useTrackedMemo(() => {
    if (!display) return stackedMarkerGeoJson
    const masked = maskLocationMarkerParts(stackedMarkerGeoJson, display)
    return display.markerPositions
      ? placeLocationMarkers(masked, display.markerPositions)
      : masked
  }, [
    stackedMarkerGeoJson,
    display?.markers,
    display?.labels,
    display?.markerPositions,
  ])

  // The editor draws the SELECTED item's own fill, line and arrows on its own layers, so
  // leaving it in the bulk collections paints every one of them twice — visibly, since the
  // fills are translucent and stack.
  //
  // Keyed on the outline, i.e. on SELECTION: GlobeViewEditableItem draws those layers for as
  // long as the item is selected, so anything narrower puts the double paint back.
  const isSelectedItem = React.useCallback(
    (id: string) => !!outlinedItemId && id === outlinedItemId,
    [outlinedItemId],
  )
  const shapeItems = useTrackedMemo(
    () => items.filter(i => !isSelectedItem(mapViewItemKey(i.id))),
    [items, isSelectedItem],
    comparer.shallow,
  )
  const showsShapes = display?.shapes ?? true
  const fillGeoJson = useTrackedMemo(
    () => (showsShapes ? buildFillShapesGeoJson(shapeItems) : EMPTY_SHAPES),
    [shapeItems, showsShapes],
  )
  const lineGeoJson = useTrackedMemo(
    () => (showsShapes ? buildLineShapesGeoJson(shapeItems) : EMPTY_SHAPES),
    [shapeItems, showsShapes],
  )
  const arrowGeoJson = useTrackedMemo(
    () => (showsShapes ? buildArrowShapesGeoJson(shapeItems) : EMPTY_SHAPES),
    [shapeItems, showsShapes],
  )

  return (
    <>
      <Source id={SHAPE_FILL_SOURCE} type="geojson" data={fillGeoJson}>
        <Layer
          id={SHAPE_FILL_LAYER}
          type="fill"
          paint={{
            'fill-color': ['get', 'fill'],
            'fill-opacity': ['get', 'fill-opacity'],
          }}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        />
      </Source>
      <Source id={SHAPE_LINE_SOURCE} type="geojson" data={lineGeoJson}>
        <Layer
          id={SHAPE_LINE_LAYER}
          type="line"
          paint={{
            'line-color': ['get', 'stroke'],
            'line-width': ['get', 'stroke-width'],
          }}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        />
      </Source>
      <Source id={SHAPE_ARROW_LINE_SOURCE} type="geojson" data={arrowGeoJson}>
        <Layer
          id={SHAPE_ARROW_LINE_LAYER}
          type="symbol"
          layout={{
            'text-field': ['get', 'text-field'],
            'text-size': ['get', 'text-width'],
            'symbol-placement': 'line',
            'symbol-avoid-edges': true,
            'symbol-spacing': [
              'interpolate',
              ['linear'],
              ['zoom'],
              10,
              1,
              20,
              60,
            ],
            'text-rotation-alignment': 'map',
            'text-keep-upright': false,
          }}
          paint={{
            'text-color': ['get', 'text-color'],
            // Text only — the arrowheads are glyphs, not an icon-image.
            ...TEXT_OCCLUSION_PAINT,
          }}
          maxzoom={MAX_ITEM_ZOOM_LEVEL}
          minzoom={PROJECT_MARKER_ZOOM_LEVEL}
        />
      </Source>
      <GLLocationMarkerLayer
        sourceId={LOCATION_MARKERS_SOURCE}
        layerId={LOCATION_MARKERS_LAYER}
        data={layerData}
        highlightedItemId={highlightedItemId}
        liftsSelection={liftsSelection}
        yieldToLayerIds={yieldToLayerIds}
        isZoomScalingEnabled={isZoomScalingEnabled && !display?.isIconSizeFixed}
        iconScale={display?.iconScale}
        isLabelOverlapEnabled={isLabelOverlapEnabled}
        onHoverItem={onHoverItem}
        onClickItem={onClickItem}
        onClickStack={onClickStack}
        onDoubleClickItem={onDoubleClickItem}
        isOverlapForced={!!display?.forceMarkerOverlap}
      />
      {isProjectMarkerOnly && !!projectCoordinates && (
        <GLProjectAddressMarker
          latitude={projectCoordinates.latitude}
          longitude={projectCoordinates.longitude}
        />
      )}
    </>
  )
})

function buildFillShapesGeoJson(
  items: MapViewItemBase[],
): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
  return {
    type: 'FeatureCollection',
    features: items
      .filter(
        i =>
          !!i.sitemapItem.shapeCoordinates?.coordinates?.length &&
          i.globeViewItemProperties.shapeProperties?.isDisplayed &&
          (i.sitemapItem.shapeCoordinates.type !==
            SitemapItemShapeType.Polyline ||
            i.sitemapItem.shapeCoordinates.isClosed),
      )
      .map(i =>
        i.sitemapItem.shapeCoordinates.getShapePolygonFeature({
          fill: ThemeMode.getHEXColor(
            i.globeViewItemProperties.shapeProperties?.fillColor,
          ),
          'fill-opacity':
            i.globeViewItemProperties.shapeProperties?.fillOpacity,
          itemId: i.id,
        }),
      ),
  }
}

function buildLineShapesGeoJson(
  items: MapViewItemBase[],
): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
  return {
    type: 'FeatureCollection',
    features: items
      .filter(
        i =>
          !!i.sitemapItem.shapeCoordinates?.coordinates?.length &&
          i.globeViewItemProperties.shapeProperties?.isDisplayed,
      )
      .map(i =>
        i.sitemapItem.shapeCoordinates.getShapeLineStringFeature({
          stroke: ThemeMode.getHEXColor(
            i.globeViewItemProperties.shapeProperties?.lineColor,
          ),
          'stroke-width': i.globeViewItemProperties.shapeProperties?.lineWidth,
          itemId: i.id,
        }),
      ),
  }
}

// Structural, not a class: the two surfaces carry different PolyLineProperties types.
const arrowPositionOf = (item: MapViewItemBase): SitemapLineArrowPosition =>
  (
    item.globeViewItemProperties.shapeProperties as {
      arrowPosition?: SitemapLineArrowPosition
    }
  )?.arrowPosition

function buildArrowShapesGeoJson(
  items: MapViewItemBase[],
): GeoJSON.FeatureCollection<GeoJSON.Geometry> {
  return {
    type: 'FeatureCollection',
    features: items
      .filter(
        i =>
          i.sitemapItem.shapeCoordinates?.coordinates?.length > 1 &&
          i.globeViewItemProperties.shapeProperties?.isDisplayed &&
          i.globeViewItemProperties.shapeProperties?.type ===
            SitemapItemShapeType.Polyline &&
          arrowPositionOf(i) !== SitemapLineArrowPosition.None &&
          !i.sitemapItem.shapeCoordinates.isClosed,
      )
      .map(i => ({
        type: 'Feature',
        properties: {
          stroke: ThemeMode.getHEXColor(
            i.globeViewItemProperties.shapeProperties?.lineColor,
          ),
          // Guarded: a nullish lineWidth made text-size NaN, dropping the arrows.
          'text-width':
            25 +
            (i.globeViewItemProperties.shapeProperties?.lineWidth || 0) * 5,
          'text-field': [
            SitemapLineArrowPosition.StartEnd,
            SitemapLineArrowPosition.StartMiddleEnd,
          ].includes(arrowPositionOf(i))
            ? '►'
            : '◄',
          'text-color': ThemeMode.getHEXColor(
            i.globeViewItemProperties.shapeProperties?.lineColor,
          ),
          itemId: i.id,
        },
        geometry: i.sitemapItem.shapeCoordinates.getShapeLineStringFeature({})
          .geometry,
      })),
  }
}
